import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Loading from "../../components/Loading/Loading";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import {
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  TextField,
  Collapse,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import HomeIcon from "@mui/icons-material/Home";

import styles from "./ListingDetails.module.css";
import { propertyTypes } from "../../util/flStates";
// import MortgageCalculator from "./LisitngCalc/LisitingCalc";
import MortgageCalculator from "mortgage-calculator-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MapIcon from "@mui/icons-material/Map";
import ErrorIcon from "@mui/icons-material/Error";
import Map from "../../components/Map/Map";
import { useAuth } from "../../contexts/AuthContext";
import Tick from "../../assets/svg/Tick";
import ReservationModal from "./Reservation/Reservation";
import ListingImages from "./ListingImages/ListingImages";
import { DateTime } from "luxon";
import ListedOwnerMiniCard from "./ListedOwnerMiniCard";

const ListingDetails = () => {
  const navigate = useNavigate();
  const { accessToken, showLoginModal } = useAuth();

  const { listingUrl } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOldUser, setIsOldUser] = useState(false);

  const [professional, setProfessional] = useState(null);

  const [queryData, setQueryData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    helpText: "I am interested in this listing.",
  });

  const [expanded, setExpanded] = useState({
    mortgageCalculator: false,
  });

  const [enquiryPosted, setEnquiryPosted] = useState(false);
  const [openProperties, setOpenProperties] = useState([]); // Open properties

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [listingUserId, setListingUserId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [listedOwnerDetails, setListedOwnerDetails] = useState(null);

  useEffect(() => {
    // After fetching listing data (listing.id)
    if (listing?.id) {
      axios
        .get(
          `${import.meta.env.VITE_BACKEND_URL}/listings/getListedUserInfo/${
            listing.id
          }`
        )
        .then((res) => {
          if (res.data?.success && res.data.listing) {
            setListingUserId(res.data.listing.user_id);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch listing user info:", err);
        });
    }
  }, [listing?.id]);

  const handleOpenModal = () => {
    if (!accessToken) {
      showLoginModal();
      return;
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const showErrorMessage = (text) => {
    // toast("Check!");
    toast.error(text, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      // draggable: true,
      progress: undefined,
      theme: "colored",
      // transition: Bounce,
    });
  };

  const handleViewAllListings = () => {
    navigate(`/Buy`);
  };

  const handleQueryDataChange = () => {
    const { name, value, type, files } = event.target;
    setQueryData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const dropDownIconStyleMortgageCalculator = {
    transform: expanded.mortgageCalculator ? "rotate(180deg)" : "",
    transition: "transform 150ms ease", // smooth transition
  };

  const handleExpandClick = (updatedState) => {
    // console.log("expanded: ", expanded);
    setExpanded((prevState) => ({
      ...prevState,
      [updatedState]: !prevState[updatedState],
    }));
    // console.log("updatedState: ", updatedState);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const getPropertyTypeLabel = (typeId) => {
    const type = Object.values(propertyTypes).find(
      (type) => type.id === parseInt(typeId)
    );
    return type ? type.label : "Unknown";
  };

  const isValidEmail = useCallback((email) => {
    // Define a regular expression pattern for email validation.
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  }, []);

  const isValidPhone = useCallback((phone) => {
    const pattern = /^\d{10,10}$/;
    return pattern.test(phone);
  }, []);

  const handleQuerySubmit = async () => {
    // console.log("Do the query here");
    // console.log("queryData: ", queryData);

    if (!queryData.fullName) {
      showErrorMessage("Please provide your Full Name");
      return;
    }

    if (!queryData.email) {
      showErrorMessage("Please provide your Email");
      return;
    }

    // email checker
    if (!isValidEmail(queryData.email)) {
      showErrorMessage("Please provide a valid Email format");
      return;
    }

    if (queryData.phoneNumber && !isValidPhone(queryData.phoneNumber)) {
      showErrorMessage("Please provide a valid Phone Number");
      return;
    }

    if (!queryData.helpText) {
      showErrorMessage("Please provide how we can help you!");
      return;
    }

    try {
      const requestData = {
        fullName: queryData.fullName,

        email: queryData.email,
        phoneNumber: queryData.phoneNumber,
        helpText: queryData.helpText,
        listingId: listing.id,
        listingUrl: listingUrl,
      };

      // console.log("accessToken: ", accessToken);
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/listings/enquiry`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      // console.log("response: ", response);

      if (response.status === 200) {
        if (
          response.data &&
          "success" in response.data &&
          response.data.success
        ) {
          // successfylly posted the query
          // console.log("response: ", response);

          // show that ticket has been successfully raised
          setEnquiryPosted(true);
        } else {
          var errorMsg =
            response.data && response.data.message
              ? response.data.message
              : "Some error occurred while posting your query. Please try again!";
          showErrorMessage(errorMsg);
          var responseError =
            response.data && response.data.error
              ? response.data.error
              : response.status + " error";
          // console.log(responseError);
        }
      } else {
        // Handle authentication error (e.g., display an error message)
        var errorMsg =
          response.data && response.data.message
            ? response.data.message
            : "Some error occurred while posting your query. Please try again!";
        showErrorMessage(errorMsg);
        var responseError =
          response.data && response.data.error
            ? response.data.error
            : response.status + " error";
        // console.log(responseError);
        // You can display an error message to the user.
      }
    } catch (error) {
      console.error("Error posting the query:", error);
    }
  };

  useEffect(() => {
    const fetchListingDetails = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/listings/${listingUrl}`
        );

        if (response.status === 200 && response.data?.success) {
          const listingData = response.data?.listing || {};

          if (listingData) {
            setListing(listingData);

            // Fetch open properties as soon as we get the listing data
            if (listingData.id) {
              fetchOpenProperties(listingData.id);
            }
            // Call the user details API and update property view
            if (listingData?.id && accessToken) {
              fetchUserDetailsAndUpdateView(
                listingData.property_id,
                listingData.id
              );
            }
          }
        } else {
          const errorMsg =
            response.data?.message ||
            "Some error occurred while fetching the listing. Please try again!";
          // console.log(response.data?.error || `${response.status} error`);
          showErrorMessage(errorMsg);
        }
        setLoading(false);
      } catch (error) {
        showErrorMessage("Failed to fetch listing details");
        setLoading(false);
        console.error("Error fetching listing details: ", error);
      }
    };

    // Function fito fetch open properties based on listing ID
    const fetchOpenProperties = async (listingId) => {
      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_URL
          }/listings/open-property/${listingId}`
        );
        if (response.data.success) {
          setOpenProperties(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching open properties:", error);
      }
    };

    const fetchUserDetailsAndUpdateView = async (propertyId, listingId) => {
      try {
        if (!propertyId || !accessToken) return;
        const userResponse = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/user/details`,
          {},
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );

        if (userResponse.status === 200 && userResponse.data?.success) {
          const user = userResponse.data?.user;
          if (user) {
            const userId = user.id;

            setCurrentUserId(userId);

            // Update property view
            await updatePropertyView(propertyId, listingId, userId);

            const fullName = [user.first_name, user.middle_name, user.last_name]
              .filter((name) => name)
              .join(" ");

            setQueryData((prev) => ({
              ...prev,
              fullName: fullName,
              email: user.email,
              phoneNumber: user.phone_number,
            }));

            setIsOldUser(true);
          }
        } else {
          const errorMsg =
            userResponse.data?.message ||
            "Some error occurred while fetching your details. Please try again!";
          // console.log(
          //   userResponse.data?.error || `${userResponse.status} error`
          // );
          showErrorMessage(errorMsg);
        }
      } catch (error) {
        console.error("Error fetching user details: ", error);
      }
    };

    const updatePropertyView = async (propertyId, listingId, userId) => {
      try {
        const viewResponse = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/listings/update-property-view`,
          {
            property_id: propertyId,
            user_id: userId,
            listing_id: listingId,
          }
        );

        if (viewResponse.status === 201) {
          // console.log("Property view updated successfully:", viewResponse.data);
        } else {
          console.error("Failed to update property view:", viewResponse.data);
        }
      } catch (error) {
        console.error("Error updating property view:", error);
      }
    };

    fetchListingDetails();
  }, [listingUrl, accessToken]);

  useEffect(() => {
    if (!listing) return;

    const proId = listing?.pro_id ?? null;
    const landlordId = listing?.landlord_id ?? null;

    // prefer pro_id if both exist
    const params =
      proId != null
        ? { pro_id: proId }
        : landlordId != null
        ? { landlord_id: landlordId }
        : null;

    if (!params) return;

    const controller = new AbortController();

    (async () => {
      try {
        const { data } = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/professional/header`,
          { params, signal: controller.signal } // <-- params go here
        );
        if (data?.success && data.professional) {
          setListedOwnerDetails(data.professional);
        }
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Failed to fetch listing user info:", err);
        }
      }
    })();

    return () => controller.abort();
  }, [listing?.pro_id, listing?.landlord_id]);

  const formatZonedTime = (timestamp, zone) => {
    const fallbackZone = zone || "UTC";
    const dt = DateTime.fromISO(timestamp, { zone: fallbackZone });

    return dt.isValid ? dt.toFormat("ff") : "Invalid Date";
  };

  const handleContactLandlord = () => {
    // e.preventDefault();
    // check if the user is logged in
    if (!accessToken) {
      // user is not logged in
      // prompt login/signup
      showLoginModal();

      // @TODO: check redirect
    }

    console.log("listing: ", listing);
    // navigate to dash chat
    // console.log("navigate to chat");
    navigate(`/MyFirstLand?category=messages`, {
      state: {
        targetUser: {
          id: listing.partner_user_id,
          first_name: listing.partner_first_name,
          last_name: listing.partner_last_name,
          photo_url: listing.partner_photo_url,
          tags: "Realtor, Broker",
        },
        targetMessage: `Hello ${listing.partner_first_name}, I'm interested in your property: ${listing.title}.`,
      },
    });
  };

  return (
    <div>
      <Navbar />
      <div className={styles.container}>
        {loading ? (
          <Loading />
        ) : listing && listing.id ? (
          <Paper elevation={3} className={styles.listingDetailsContainer}>
            <div className={styles.headerContainer}>
              <div className={styles.titleAndChips}>
                <Typography variant="h4" gutterBottom>
                  {listing.title}
                </Typography>
                <div className={styles.chipContainer}>
                  <Chip
                    icon={<LocationOnIcon />}
                    label={`${listing.city}, ${listing.state} ${listing.zipcode}`}
                  />
                  <Chip
                    label={listing.status || "Available"} // Default to "Available" if status is not provided
                    color={
                      listing.status === "Sold"
                        ? "error"
                        : listing.status === "Off Market"
                        ? "warning"
                        : "success"
                    }
                    className={styles.statusChip}
                  />
                </div>
              </div>
            </div>

            <ListingImages listing={listing} />

            <Grid container spacing={3} className={styles.listingDetailsGrid}>
              <Grid item xs={12} md={8}>
                <div elevation={2} className={styles.listingPriceDetails}>
                  <Typography variant="h5" gutterBottom>
                    <LocalOfferIcon />{" "}
                    {listing.listing_type === "2" ? "For Sale" : "For Rent"}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {listing.listing_type === "2"
                      ? formatPrice(listing.sell_price)
                      : `${formatPrice(listing.rent_price)}/month`}
                  </Typography>
                  {listing.listing_type === "2" && (
                    <Typography>
                      <strong>HOA Fee:</strong>{" "}
                      {formatPrice(listing.sell_hoa_fee)}/month
                    </Typography>
                  )}
                  {listing.listing_type === "1" &&
                    listing.rent_lease_length && (
                      <Typography>
                        <strong>Lease Length:</strong>{" "}
                        {listing.rent_lease_length} months
                      </Typography>
                    )}
                  {listing.listing_type === "1" && listing.rent_pet_policy && (
                    <Typography>
                      <strong>Pet Policy:</strong> {listing.rent_pet_policy}
                    </Typography>
                  )}
                  <Typography>
                    <strong>Available From:</strong>{" "}
                    {new Date(listing.available_from).toLocaleDateString()}
                  </Typography>
                </div>
                <div className={styles.listingQuickFacts}>
                  <Typography variant="h6" gutterBottom>
                    Quick Facts
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs="auto">
                      <Chip
                        icon={<BedIcon />}
                        label={`${listing.bedrooms} Beds`}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <Chip
                        icon={<BathtubIcon />}
                        label={`${listing.bathrooms} Baths`}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <Chip
                        icon={<SquareFootIcon />}
                        label={`${listing.area_sqft} sqft`}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <Chip
                        icon={<HomeIcon />}
                        label={getPropertyTypeLabel(listing.property_type)}
                      />
                    </Grid>
                    <Grid item xs="auto">
                      <Chip
                        icon={<CalendarTodayIcon />}
                        label={`Built in ${listing.year_built}`}
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              {currentUserId !== listingUserId && (
                <Grid item xs={12} md={4}>
                  <div className={styles.actionButtonsContainer}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleContactLandlord();
                      }}
                      className={styles.actionButton}
                    >
                      {listing.pro_id
                        ? "Contact Professional"
                        : listing.landlord_id
                        ? "Contact Landlord"
                        : "Contact Agent"}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleOpenModal}
                      className={styles.actionButton}
                    >
                      Schedule a Visit
                    </Button>
                  </div>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                {/* your existing Open Property Timeline Box goes here */}
              </Grid>
              <Box className={styles.openPropertyTimeline}>
                <Typography variant="h6" gutterBottom>
                  Open Property Timeline
                </Typography>

                {openProperties.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No open properties available.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {openProperties.map((property, idx) => (
                      <React.Fragment key={property.id}>
                        <ListItem className={styles.timelineItem}>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1">
                                {formatZonedTime(
                                  property.open_from,
                                  property.timezone
                                )}{" "}
                                 ↔ 
                                {formatZonedTime(
                                  property.open_to,
                                  property.timezone
                                )}
                              </Typography>
                            }
                            secondary={
                              property.comments && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {property.comments}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                        {idx < openProperties.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
              <Grid item xs={12} md={4}>
                <ListedOwnerMiniCard owner={listedOwnerDetails} />
              </Grid>
            </Grid>

            <div className={styles.ldSection}>
              <div className={styles.ldSectionHeader}>
                <MapIcon />
                <div className={styles.ldSectionTitle}>Address</div>
              </div>
              <div className={styles.ldAddressSectionChips}>
                {!listing.hide_address && (
                  <div className={styles.ldAddressSectionChip}>
                    <div className={styles.label}>Street</div>
                    <div className={styles.value}>
                      {listing.street2
                        ? `${listing.street}, ${listing.street2}`
                        : `${listing.street}`}
                    </div>
                  </div>
                )}
                <div className="ldAddressSectionChips">
                  <div className="label">City</div>
                  <div className="value">{listing.city}</div>
                </div>
                <div className="ldAddressSectionChips">
                  <div className="label">State</div>
                  <div className="value">{listing.state}</div>
                </div>
                <div className="ldAddressSectionChips">
                  <div className="label">Zipcode</div>
                  <div className="value">{listing.zipcode}</div>
                </div>
                <div className="ldAddressSectionChips">
                  <div className="label">Landmark</div>
                  <div className="value">{listing.landmark}</div>
                </div>
              </div>
              {!listing.hide_address && (
                <div className="ldAddressMap">
                  <Map propertyData={listing} />
                </div>
              )}
            </div>

            <div className="ldSection">
              <div
                className="ldSectionHeader"
                onClick={() => handleExpandClick("mortgageCalculator")}
              >
                <CalendarMonthIcon />
                <div className="ldSectionTitle">Mortgage Calculator</div>
                <div
                  className="ldSectionDropdownBtn"
                  style={dropDownIconStyleMortgageCalculator}
                >
                  <ExpandMoreIcon fontSize="large" />
                </div>
              </div>
              <Collapse
                in={expanded.mortgageCalculator}
                timeout="auto"
                unmountOnExit
              >
                <MortgageCalculator
                  price={listing.sell_price}
                  downPayment={Math.round(listing.sell_price * 0.2)}
                />
              </Collapse>
            </div>
          </Paper>
        ) : (
          <div className={styles.noListingContainer}>
            <div className={styles.noListingText}>
              Unable to fetch this listing. Please check our other offerings.
            </div>
            <Button
              variant="contained"
              size="large"
              onClick={handleViewAllListings}
            >
              View All Listings
            </Button>
          </div>
        )}
      </div>
      <Footer />
      <ReservationModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        listing={listing}
      />
    </div>
  );
};

export default ListingDetails;
